import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { MenuService, NavItem } from "@core/services/utils/menu.service";
import { TranslatePipe } from "@ngx-translate/core";
import { BEBasicService } from "@services/be-basic.service";
import { BeUsersService } from "@services/business/user-services/be-users.service";
import { MobileMenuComponent } from "@ui/components/mobile-menu/mobile-menu.component";
import { ButtonIconComponent } from "@ui/controls/button-icon/button-icon.component";
import { IconDisplayComponent } from "@ui/ui-helpers/display-formats/icon-display/icon-display.component";
import { filter, Subject, takeUntil } from "rxjs";
import { NotificationEvent } from "../lc-header/header-icons/notifications-panel/notification-item/types";
import { NotificationsPanelComponent } from "../lc-header/header-icons/notifications-panel/notifications-panel.component";

@Component({
  selector: "app-lc-header-mobile",
  templateUrl: "./lc-header-mobile.component.html",
  styleUrl: "./lc-header-mobile.component.scss",
  imports: [
    IconDisplayComponent,
    ButtonIconComponent,
    TranslatePipe,
    MobileMenuComponent,
    NotificationsPanelComponent,
  ],
})
export class LcHeaderMobileComponent implements OnDestroy {
  protected apiService = inject(BEBasicService);
  private usersService = inject(BeUsersService);
  private router = inject(Router);
  private menuService = inject(MenuService);

  @Input() public isUserLoggedIn = false;
  @Input() public newMessages = false;
  @Input() public notifications: NotificationEvent[] = [];
  @Output() public markNotificationAsReadEvent =
    new EventEmitter<NotificationEvent>();

  protected notificationsOpen = false;
  protected configurationOpen = false;
  protected mobileMenuOpen = false;
  protected menuItems: NavItem[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.apiService
      .isUserLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.isUserLoggedIn = data;
      });

    this.menuService
      .getMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe(menu => {
        this.menuItems = menu;
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.mobileMenuOpen = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToRecordSelector(): void {
    const { isReviewer, isChannelM2M } = this.usersService.getRoleFlags();
    if (isReviewer || isChannelM2M) {
      this.toggleMobileMenu();
    } else {
      this.router.navigate(["/records"]);
    }
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  onMobileMenuClose(): void {
    this.mobileMenuOpen = false;
  }

  openNotifications(): void {
    this.notificationsOpen = true;
  }

  closeNotifications(): void {
    this.notificationsOpen = false;
  }

  markNotificationAsRead(notification: NotificationEvent): void {
    this.markNotificationAsReadEvent.emit(notification);
  }

  goToFaqs(): void {
    this.router.navigate(["/faq"]);
  }

  goToProfile(): void {
    const { isReviewer } = this.usersService.getRoleFlags();
    if (isReviewer) {
      this.router.navigate(["/dashboard/management/configuration"]);
    } else {
      this.router.navigate(["/profile"]);
    }
  }

  protected get hasNotifications(): boolean {
    return this.notifications?.length > 0;
  }

  public get isOnlyClient(): boolean {
    return this.usersService.isOnlyClient();
  }
}
